import { Button, Tag, Collapse, List, Spin, Tooltip } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { ContextApp } from '../../contexts/ContextApp';
import { IProcessItem } from '../../interfaces/Workflow';
import { PaperClipOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import './ViewWorkflowModal.less';
import GraphqlService from '../../services/graphql/GraphqlService';

const { Panel } = Collapse;

interface FinishedActivityDetailsProps {
  data: IProcessItem | null;
  formattedInitialDate: string | undefined;
  formattedDueDate: string | undefined;
  onCancel?: () => void;
}

interface IWorkflowFile {
  id: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  url: string;
  its: string;
}

const FinishedActivityDetails = (props: FinishedActivityDetailsProps) => {
  const { t } = useContext(ContextApp);
  const { data, formattedDueDate, formattedInitialDate, onCancel } = props;
  const [files, setFiles] = useState<IWorkflowFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(false);
  const { customRequest, Query } = GraphqlService();

  useEffect(() => {
    if (data?.id) {
      fetchFilesForTask(data.id);
    }
  }, [data?.id]);

  const fetchFilesForTask = async (taskId: string) => {
    if (!taskId) return;
    
    console.log('Fetching files for task:', taskId);
    setLoadingFiles(true);
    try {
      const response = await customRequest({
        query: Query.getWorkflowFilesByTask,
        variables: {
          input: {
            taskId: taskId,
            workflowInstanceId: data?.id
          }
        }
      });
      
      console.log('Raw response:', response);
      
      if (response.errors) {
        console.error('GraphQL Errors:', response.errors);
        throw new Error('Error al cargar los archivos');
      }
      
      console.log('Response data:', response);
      
      if (response?.files) {
        console.log('Files found:', response.files);
        setFiles([...(response.files || [])]);
      } else {
        console.log('No files found for task', taskId);
        setFiles([]);
      }
    } catch (error) {
      console.error('Error in fetchFilesForTask:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  return (
    <>
      <div className="finished-activity-container">
        <div className="finish-activity-title-and-state-container">
          <h5 style={{ marginBottom: '-2px', width: '70%' }}>
            {data?.activity}
          </h5>
          <div className="status-container">
            <label style={{ marginRight: '10px' }}>{t('entity.status')}:</label>
            <Tag color={data?.status.color}>{data?.status.name}</Tag>
          </div>
        </div>
        <span className="activity-subtitle">
          Pertenece al flujo: {data?.process} - Motivo:{' '}
          {data?.reason ? data?.reason : t('entity.noReason')}
        </span>

        <div style={{ marginTop: '15px' }}>
          <div className="initial-date-container">
            <label className="date-label">Asignado por:</label>
            <span>{data?.assigner_name}</span>
          </div>

          <div className="initial-date-container">
            <label className="label-info-styles">{t('entity.startdate')}:</label>
            <span>{formattedInitialDate}</span>
          </div>

          <div className="end-date-container">
            <label className="label-info-styles">{t('entity.limitDate')}:</label>
            <span>{data?.limitDate ? formattedDueDate : 'Sin asignar'}</span>
          </div>
        </div>

        <p style={{ marginTop: '15px' }}>
          <b>{data?.description}:</b>
        </p>

        {data?.previous_task_comment && (
          <>
            <div className="activity-data">
              <span>
                {data?.previous_task_comment
                  ? data?.previous_task_comment
                  : 'Sin observaciones'}
              </span>
            </div>
          </>
        )}
      </div>

      <Collapse className="collapse-panel">
        <Panel
          header="Información adicional agregada en la actividad"
          key="2"
          className="panel-style"
        >
          <div className="finished-ativity-data-container">
            <label className="label-info-styles">
              <b>{t('entity.observations')}:</b>
            </label>
            <span>{data?.comment ? data?.comment : 'Sin observaciones'}</span>
          </div>
          <div style={{ marginTop: '16px' }}>
          <label className="label-info-styles">
            <b><PaperClipOutlined /> {t('entity.attachments')}</b>
          </label>
          {loadingFiles ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <Spin size="small" />
            </div>
          ) : Array.isArray(files) && files.length > 0 ? (
            <List
              size="small"
              dataSource={files}
              renderItem={(file: IWorkflowFile) => (
                <List.Item>
                  <span style={{ color: '#999', fontSize: '12px' }}>
                    {moment(file.its).format('DD/MM/YYYY HH:mm')}
                  </span>

                  <Tooltip title={file.original_filename || file.filename}>
                    <span
                      style={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        paddingLeft: '20px',
                      }}
                    >
                      {file.original_filename || file.filename}
                    </span>
                  </Tooltip>

                  <Tooltip title={t('entity.download')}>
                    <Button
                      type="text"
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}                    
                    >
                      <DownloadOutlined style={{ color: '#03729F' }} />
                    </Button>
                  </Tooltip>
                </List.Item>
              )}
            />
          ) : (
            <p>No hay archivos adjuntos</p>
          )}
        </div>
        </Panel>
      </Collapse>

      <div className="button-container">
        <Button onClick={onCancel} type="primary">
          {t('action.close')}
        </Button>
      </div>
    </>
  );
};

export default FinishedActivityDetails;
